import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { SiteHeader } from './SiteHeader';
import { SiteFooter } from './SiteFooter';
import { ProcedureCard } from './ProcedureCard';
import { PROCEDURE_CATEGORIES, getFallbackProcedures, getProcedurePresentation } from './portfolioContent';
import './variables.css';
import './SiteHome.css';

const categoryDefs = [
  { key: 'Cílios', label: 'Cílios', icon: '👁️', title: 'Procedimentos de Cílios', desc: 'Extensões clássicas, volumes e lifting para valorizar o seu olhar com naturalidade e durabilidade.' },
  { key: 'Sobrancelha', label: 'Sobrancelhas', icon: '✨', title: 'Design & Sobrancelhas', desc: 'Mapeamento facial, henna, brow lamination e micropigmentação hiper-realista.' },
  { key: 'Maquiagem', label: 'Maquiagem', icon: '💄', title: 'Maquiagem Profissional', desc: 'Produções completas Glow e Social Luxo para formaturas, casamentos e momentos inesquecíveis.' },
  { key: 'Limpeza', label: 'Limpeza & Facial', icon: '🌸', title: 'Cuidados Faciais & Limpeza de Pele', desc: 'Tratamentos para renovação celular, desintoxicação e luminosidade da pele.' },
];

export function SiteHome({ onNavigate, usuario }) {
  const [procedures, setProcedures] = useState(getFallbackProcedures());
  const [activeCategory, setActiveCategory] = useState('all');
  const whatsappUrl = 'https://wa.me/5567999359198?text=Ola%2C%20Fran!%20Quero%20agendar%20um%20horario.%20Pode%20me%20ajudar%3F';
  const instagramUrl = 'https://www.instagram.com/franmullerbeauty/';

  useEffect(() => {
    const loadProcedures = async () => {
      try {
        const response = await axios.get('/api/servicos/portfolio');
        if (response.data && response.data.length > 0) {
          const nextProcedures = response.data.map(getProcedurePresentation);
          setProcedures(nextProcedures);
        }
      } catch (error) {
        setProcedures(getFallbackProcedures());
      }
    };

    loadProcedures();
  }, []);

  const handleSchedule = (serviceId) => {
    onNavigate('agendamento', { serviceId });
  };

  const normalizeCategory = (cat) => {
    const c = (cat || '').toLowerCase();
    if (c.includes('cilio') || c.includes('cílio') || c.includes('lash')) return 'Cílios';
    if (c.includes('sobrancelha') || c.includes('brow') || c.includes('micro')) return 'Sobrancelha';
    if (c.includes('make') || c.includes('maquiagem')) return 'Maquiagem';
    if (c.includes('limpeza') || c.includes('pele') || c.includes('facial')) return 'Limpeza';
    return cat || 'Outros';
  };

  const getFilteredProcedures = (catKey) => {
    if (catKey === 'all') return procedures;
    return procedures.filter((p) => normalizeCategory(p.category) === catKey);
  };

  return (
    <div className="site-page">
      <SiteHeader onNavigate={onNavigate} usuario={usuario} />

      {/* Hero / Top Banner */}
      <section className="site-hero">
        <div className="site-hero-container">
          <span className="site-hero-badge">✨ Espaço Fran Müller Beauty</span>
          <h1 className="site-hero-title">
            Realce sua beleza natural com sofisticação e excelência
          </h1>
          <p className="site-hero-subtitle">
            Especialista em Micropigmentação, Cílios, Sobrancelhas, Limpeza de Pele e Maquiagem.
          </p>
          <div className="site-hero-buttons">
            <button
              type="button"
              className="site-hero-btn-primary"
              onClick={() => onNavigate('agendamento')}
            >
              📅 Agendar Meu Horário
            </button>
            <a
              href="#procedimentos"
              className="site-hero-btn-secondary"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('procedimentos')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Conhecer Procedimentos & Valores
            </a>
          </div>
        </div>
      </section>

      {/* Sobre a Fran */}
      <section id="sobre" className="site-section site-section-bio">
        <div className="site-bio-container">
          <div className="site-bio-photo-wrap">
            <img
              src="/images/fran-profile.jpg"
              alt="Franci Kelli Müller"
              className="site-bio-photo"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </div>
          <div className="site-bio-content">
            <span className="site-section-tag">Sobre Mim</span>
            <h2 className="site-section-title">Franci Kelli Müller</h2>
            <p className="site-bio-role">ESTETICISTA & MAQUIADORA</p>
            <p className="site-bio-text">
              Olá! Eu sou <strong>Franci Kelli Müller</strong>, esteticista, maquiadora e designer de beleza. Trabalho na área da estética há mais de 10 anos, sou formada em estética e especializada em extensão de cílios, micropigmentação, design de sobrancelhas, maquiagem profissional e cuidados faciais.
            </p>
            <p className="site-bio-text">
              Apaixonada por realçar a beleza e elevar a autoestima, meu compromisso é proporcionar um atendimento acolhedor, sofisticado e personalizado com técnicas modernas e produtos de altíssima qualidade.
            </p>
            <div className="site-bio-quote">
              “Minha paixão é valorizar a sua beleza natural e ver o brilho em seu olhar.”
            </div>
          </div>
        </div>
      </section>

      {/* Procedimentos e Valores */}
      <section id="procedimentos" className="site-section site-section-procedures">
        <div className="site-container">
          <div className="site-section-header">
            <span className="site-section-tag">Catálogo Exclusivo</span>
            <h2 className="site-section-title">Procedimentos & Cuidados</h2>
            <p className="site-section-subtitle">
              Navegue pelos procedimentos por categoria e escolha o atendimento perfeito para valorizar a sua beleza.
            </p>
          </div>

          {/* Filtro por Categorias */}
          <div className="site-category-filters">
            {PROCEDURE_CATEGORIES.map((cat) => {
              const count = getFilteredProcedures(cat.key).length;
              return (
                <button
                  key={cat.key}
                  type="button"
                  className={`site-category-btn ${activeCategory === cat.key ? 'site-category-btn-active' : ''}`}
                  onClick={() => setActiveCategory(cat.key)}
                >
                  <span className="site-category-icon">{cat.icon}</span>
                  <span>{cat.label}</span>
                  <span className="site-category-count">({count})</span>
                </button>
              );
            })}
          </div>

          {/* Exibição por Grupos */}
          {activeCategory === 'all' ? (
            <div className="site-procedure-groups">
              {categoryDefs.map((group) => {
                const groupProcedures = getFilteredProcedures(group.key);
                if (groupProcedures.length === 0) return null;

                return (
                  <div key={group.key} className="site-procedure-group">
                    <div className="site-group-header">
                      <div className="site-group-title-wrap">
                        <span className="site-group-icon">{group.icon}</span>
                        <div>
                          <h3 className="site-group-title">{group.title}</h3>
                          <p className="site-group-desc">{group.desc}</p>
                        </div>
                      </div>
                      <span className="site-group-badge">{groupProcedures.length} {groupProcedures.length === 1 ? 'procedimento' : 'procedimentos'}</span>
                    </div>

                    <div className="site-procedures-grid">
                      {groupProcedures.map((proc) => (
                        <ProcedureCard
                          key={proc.id}
                          id={proc.id}
                          title={proc.title}
                          description={proc.description}
                          images={proc.images}
                          imageUrl={proc.imageUrl}
                          formattedPrice={proc.formattedPrice}
                          onSchedule={handleSchedule}
                          whatsappUrl={whatsappUrl}
                          instagramUrl={instagramUrl}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="site-procedures-grid site-filtered-grid">
              {getFilteredProcedures(activeCategory).map((proc) => (
                <ProcedureCard
                  key={proc.id}
                  id={proc.id}
                  title={proc.title}
                  description={proc.description}
                  images={proc.images}
                  imageUrl={proc.imageUrl}
                  formattedPrice={proc.formattedPrice}
                  onSchedule={handleSchedule}
                  whatsappUrl={whatsappUrl}
                  instagramUrl={instagramUrl}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Agendamento e Contato */}
      <section id="agendar" className="site-section site-section-schedule">
        <div className="site-container site-schedule-box">
          <span className="site-section-tag">Agendamento Fácil</span>
          <h2 className="site-section-title">Como você prefere agendar?</h2>
          <p className="site-section-subtitle">
            Escolha o canal de sua preferência para agendar seu horário com praticidade.
          </p>

          <div className="site-schedule-channels">
            <div className="site-channel-card" onClick={() => onNavigate('agendamento')}>
              <div className="site-channel-icon site-icon-calendar">📅</div>
              <h3>Online no Site</h3>
              <p>Escolha a data, horário e serviço disponível em tempo real.</p>
              <button type="button" className="site-channel-btn">Agendar Online →</button>
            </div>

            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="site-channel-card"
            >
              <div className="site-channel-icon site-icon-whatsapp">
                <img src="/images/whatsapp-icon.png" alt="WhatsApp" />
              </div>
              <h3>WhatsApp</h3>
              <p>Fale diretamente comigo para tirar dúvidas ou agendar.</p>
              <span className="site-channel-btn">Conversar no WhatsApp →</span>
            </a>

            <a
              href={instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="site-channel-card"
            >
              <div className="site-channel-icon site-icon-instagram">
                <img src="/images/instagram-icon.png" alt="Instagram" />
              </div>
              <h3>Instagram</h3>
              <p>Acompanhe antes e depois, dicas e mande uma DM.</p>
              <span className="site-channel-btn">Ver no Instagram →</span>
            </a>
          </div>
        </div>
      </section>

      {/* Banner de Acesso ao ERP */}
      <section className="site-erp-banner">
        <div className="site-container site-erp-banner-content">
          <div>
            <h3>🔐 Acesso Administrativo e Gestão</h3>
            <p>Acesse o painel do ERP Fran Beauty para gerenciar agendamentos, clientes, financeiro e relatórios.</p>
          </div>
          <button
            type="button"
            className="site-erp-banner-btn"
            onClick={() => onNavigate(usuario ? 'erp' : 'login')}
          >
            {usuario ? '📊 Ir para o Painel ERP' : '✨ Acessar o ERP'}
          </button>
        </div>
      </section>

      <SiteFooter onNavigate={onNavigate} usuario={usuario} />
    </div>
  );
}
